import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ArrowRight, Palette, Award, Image, Star, Heart, Users, Globe, Quote, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { getSiteSettings } from '@/lib/siteSettings';
import { sortFeatured, HOME_FEATURED_COUNT } from '@/lib/featured';
import SEO from '@/components/SEO';
import ProgressiveImage from '@/components/ProgressiveImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = { Image, Palette, Award, Star, Heart, Users, Globe };

const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.12 } } };
const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

/* Animated Counter */
const Counter = ({ end, suffix = '', label, icon: Icon }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && !started) setStarted(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let current = 0;
    const step = Math.max(1, Math.ceil(end / 40));
    const timer = setInterval(() => {
      current += step;
      if (current >= end) { setCount(end); clearInterval(timer); }
      else setCount(current);
    }, 30);
    return () => clearInterval(timer);
  }, [started, end]);

  return (
    <div ref={ref} className="text-center" data-testid={`counter-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <Icon size={28} className="mx-auto mb-3 text-accent" />
      <p className="font-serif text-4xl sm:text-5xl font-light tracking-tight">
        {count}{suffix}
      </p>
      <p className="font-sans text-xs sm:text-sm uppercase tracking-[0.2em] text-muted-foreground mt-2">{label}</p>
    </div>
  );
};

const Home = () => {
  const [featuredArtworks, setFeaturedArtworks] = useState([]);
  const [allArtworks, setAllArtworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [pageContent, setPageContent] = useState(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [failedVideos, setFailedVideos] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '30%']);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, artworksRes, contentRes, testimonialsRes] = await Promise.all([
          getSiteSettings(),
          axios.get(`${API}/artworks`),
          axios.get(`${API}/page-content/home`).catch(() => ({ data: null })),
          axios.get(`${API}/testimonials?featured=true`).catch(() => ({ data: [] }))
        ]);
        setSettings(settingsRes.data);
        setAllArtworks(artworksRes.data);
        setFeaturedArtworks(sortFeatured(artworksRes.data).slice(0, HOME_FEATURED_COUNT));
        setPageContent(contentRes.data);
        setTestimonials(testimonialsRes.data || []);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const nextTestimonial = useCallback(() => {
    setCurrentTestimonial(prev => (prev + 1) % testimonials.length);
  }, [testimonials.length]);

  const prevTestimonial = useCallback(() => {
    setCurrentTestimonial(prev => (prev - 1 + testimonials.length) % testimonials.length);
  }, [testimonials.length]);

  useEffect(() => {
    if (testimonials.length <= 1) return;
    const timer = setInterval(nextTestimonial, 6000);
    return () => clearInterval(timer);
  }, [testimonials.length, nextTestimonial]);

  useEffect(() => {
    if (settings?.hero_videos?.length > 1) {
      const interval = setInterval(() => {
        setCurrentVideoIndex(prev => (prev + 1) % settings.hero_videos.length);
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [settings]);

  const c = pageContent || {};
  const stats = c.stats || [
    { value: allArtworks.length || 48, suffix: '+', label: 'Artworks', icon: 'Image' },
    { value: 1500, suffix: '+', label: 'Film Posters', icon: 'Palette' },
    { value: 5, suffix: '', label: 'Exhibitions', icon: 'Award' }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEO
        title="Gangadhar Som - Renowned Indian Artist | Buy Original Paintings & Prints"
        description="Discover and buy original paintings by Gangadhar Som — renowned professional artist with 40+ years in fine arts and South Indian cinema. Creator of 46 Lord Hanuman life history paintings at Karya Siddhi Hanuman Temple, Dallas. Oil paintings, devotional art, portraits, prints, and commissions."
        keywords="Gangadhar Som, Indian artist, Hanuman paintings, devotional art, buy paintings online, Krishna paintings, oil paintings, acrylics, watercolors, film poster art, temple paintings, portrait painting, art prints, 900 movies"
        url={window.location.origin}
      />

      {/* ===================== HERO ===================== */}
      <section ref={heroRef} className="relative h-screen overflow-hidden" data-testid="hero-section">
        <motion.div className="absolute inset-0" style={{ y: heroY }}>
          {(() => {
            // relative /api/ URLs are our own uploads; videos that fail to load fall back to the hero image
            const validVideos = (settings?.hero_videos || []).filter(v => v && v.url && /^(https?:\/\/|\/api\/)/.test(v.url) && !failedVideos.includes(v.url));
            if (validVideos.length > 0) {
              return (
                <>
                  {validVideos.map((video, i) => (
                    <video
                      key={video.url} autoPlay loop muted playsInline
                      src={video.url}
                      onError={() => setFailedVideos(prev => prev.includes(video.url) ? prev : [...prev, video.url])}
                      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${i === currentVideoIndex % validVideos.length ? 'opacity-100' : 'opacity-0'}`}
                      style={{ filter: 'brightness(0.6)' }}
                    />
                  ))}
                </>
              );
            }
            const heroImg = settings?.hero_image_url || c.hero_background_image || '';
            return heroImg ? (
              <div className="animate-slow-zoom h-full">
                <img src={heroImg} alt="Hero artwork" className="w-full h-full object-cover" />
              </div>
            ) : (
              <div className="h-full bg-stone-800" />
            );
          })()}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        </motion.div>

        <motion.div
          style={{ opacity: heroOpacity }}
          className="relative h-full max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24 flex items-end pb-16 sm:pb-24"
        >
          <motion.div
            initial="hidden" animate="visible" variants={stagger}
            className="max-w-3xl w-full"
          >
            <motion.div
              variants={fadeUp}
              data-testid="hero-title"
            >
              {settings?.hero_title_image_url ? (
                <img src={settings.hero_title_image_url} alt={settings?.hero_title || 'Gangadhar Som'}
                  className="max-h-16 sm:max-h-20 md:max-h-24 lg:max-h-28 w-auto object-contain mb-4 sm:mb-6"
                  data-testid="hero-title-image" />
              ) : (
                <h1 className="font-serif text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tight text-white mb-4 sm:mb-6">
                  {settings?.hero_title || 'Gangadhar Som'}
                </h1>
              )}
            </motion.div>
            <motion.p
              variants={fadeUp}
              className="font-sans text-sm sm:text-base md:text-lg text-white/90 mb-6 sm:mb-8 max-w-2xl leading-relaxed"
              data-testid="hero-subtitle"
            >
              {settings?.hero_subtitle || "A renowned artist with over four decades of mastery in oil paintings, acrylics, and digital art. From designing publicity for 900+ South Indian films to creating monumental temple paintings across India and the USA — honored as Datta Peetha Asthan Chitrakara."}
            </motion.p>
            <motion.div variants={fadeUp}>
              <Link
                to={c.hero_cta_link || "/gallery"} data-testid="view-gallery-button"
                className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-accent text-white font-sans uppercase tracking-[0.2em] text-xs sm:text-sm font-medium rounded-full hover:bg-accent/90 transition-all btn-hover"
              >
                {c.hero_cta_text || 'View Gallery'} <ArrowRight size={16} />
              </Link>
            </motion.div>
          </motion.div>

          {settings?.hero_videos?.length > 1 && (
            <div className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
              {settings.hero_videos.map((_, i) => (
                <button key={i} onClick={() => setCurrentVideoIndex(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${i === currentVideoIndex ? 'bg-white w-8' : 'bg-white/40 w-3 hover:bg-white/70'}`}
                />
              ))}
            </div>
          )}
        </motion.div>
      </section>

      {/* ===================== STATS COUNTERS ===================== */}
      <section className="py-16 sm:py-20 border-b border-border" data-testid="stats-section">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 md:px-12">
          <div className={`grid grid-cols-${Math.min(stats.length, 4)} gap-6 md:gap-12`}
            style={{ gridTemplateColumns: `repeat(${Math.min(stats.length, 4)}, minmax(0, 1fr))` }}>
            {stats.map((stat, i) => (
              <Counter key={i} end={stat.value || 0} suffix={stat.suffix || ''} label={stat.label || ''}
                icon={ICON_MAP[stat.icon] || Image} />
            ))}
          </div>
        </div>
      </section>

      {/* ===================== FEATURED WORKS ===================== */}
      {!loading && featuredArtworks.length > 0 && (
        <section className="py-16 sm:py-24 md:py-32 noise-overlay" data-testid="featured-works-section">
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              className="mb-10 sm:mb-16"
            >
              <span className="font-sans uppercase tracking-[0.2em] text-xs font-medium text-muted-foreground">
                {c.featured_label || 'Featured Works'}
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light tracking-tight mt-3">
                {c.featured_title || 'Selected Artworks'}
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 md:gap-12">
              {featuredArtworks.map((artwork, index) => (
                <motion.div
                  key={artwork.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ delay: index * 0.1 }}
                  className="group"
                  data-testid={`featured-artwork-${index}`}
                >
                  <Link to="/gallery">
                    <div className="artwork-frame relative aspect-[4/5] overflow-hidden mb-4 protected-image select-none">
                      <ProgressiveImage
                        src={artwork.thumb_url || artwork.image_url}
                        placeholder={artwork.placeholder}
                        alt={`${artwork.title} — ${artwork.medium || 'painting'} by Gangadhar Som`}
                        className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105 pointer-events-none select-none"
                      />
                      <div className="absolute inset-0 z-[1]" />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-500 z-[2]" />
                    </div>
                    <h3 className="font-serif text-xl sm:text-2xl md:text-3xl font-light mb-1">{artwork.title}</h3>
                    {artwork.year && <p className="font-sans text-sm text-muted-foreground">{artwork.year}</p>}
                  </Link>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0 }} whileInView={{ opacity: 1 }}
              viewport={{ once: true }} className="mt-10 sm:mt-16 text-center"
            >
              <Link
                to="/gallery" data-testid="view-all-works-button"
                className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 sm:py-4 border-2 border-accent text-accent font-sans uppercase tracking-[0.2em] text-xs sm:text-sm font-medium rounded-full hover:bg-accent hover:text-white transition-all"
              >
                View All Works <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* ===================== TESTIMONIALS CAROUSEL ===================== */}
      {testimonials.length > 0 && (
        <section className="py-16 sm:py-24 md:py-32 noise-overlay" data-testid="testimonials-carousel-section">
          <div className="max-w-screen-xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              className="text-center mb-10 sm:mb-14"
            >
              <span className="font-sans uppercase tracking-[0.2em] text-xs font-medium text-muted-foreground">
                What People Say
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light tracking-tight mt-3">
                Testimonials
              </h2>
            </motion.div>

            <div className="relative max-w-3xl mx-auto">
              <div className="overflow-hidden relative min-h-[200px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentTestimonial}
                    initial={{ opacity: 0, x: 40 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -40 }}
                    transition={{ duration: 0.4, ease: 'easeInOut' }}
                    className="text-center px-4 sm:px-8"
                    data-testid={`carousel-testimonial-${currentTestimonial}`}
                  >
                    <Quote size={32} className="mx-auto text-accent/20 mb-5" />
                    <p className="font-serif text-base sm:text-lg md:text-xl leading-relaxed text-foreground/85 italic mb-6">
                      "{testimonials[currentTestimonial]?.testimonial_text}"
                    </p>
                    <div className="flex justify-center gap-0.5 mb-3">
                      {[1, 2, 3, 4, 5].map(s => (
                        <Star key={s} size={14}
                          className={s <= (testimonials[currentTestimonial]?.rating || 5) ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/20'} />
                      ))}
                    </div>
                    <p className="font-sans text-sm font-semibold">
                      {testimonials[currentTestimonial]?.client_name}
                    </p>
                    {testimonials[currentTestimonial]?.designation && (
                      <p className="font-sans text-xs text-muted-foreground mt-0.5">
                        {testimonials[currentTestimonial]?.designation}
                      </p>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Navigation arrows */}
              {testimonials.length > 1 && (
                <>
                  <button onClick={prevTestimonial}
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 sm:-translate-x-6 p-2 rounded-full bg-card border border-border shadow-sm hover:border-accent/40 transition-colors"
                    data-testid="carousel-prev-btn">
                    <ChevronLeft size={18} className="text-muted-foreground" />
                  </button>
                  <button onClick={nextTestimonial}
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 sm:translate-x-6 p-2 rounded-full bg-card border border-border shadow-sm hover:border-accent/40 transition-colors"
                    data-testid="carousel-next-btn">
                    <ChevronRight size={18} className="text-muted-foreground" />
                  </button>
                </>
              )}

              {/* Dots */}
              {testimonials.length > 1 && (
                <div className="flex justify-center gap-2 mt-8">
                  {testimonials.map((_, i) => (
                    <button key={i} onClick={() => setCurrentTestimonial(i)}
                      className={`h-1.5 rounded-full transition-all duration-300 ${i === currentTestimonial ? 'bg-accent w-6' : 'bg-muted-foreground/20 w-2 hover:bg-muted-foreground/40'}`}
                      data-testid={`carousel-dot-${i}`} />
                  ))}
                </div>
              )}
            </div>

            <div className="text-center mt-8">
              <Link to="/testimonials" data-testid="view-all-testimonials-btn"
                className="inline-flex items-center gap-2 text-accent font-sans uppercase tracking-[0.2em] text-xs sm:text-sm font-medium hover:gap-4 transition-all">
                View All Reviews <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ===================== ABOUT PREVIEW ===================== */}
      <section className="py-16 sm:py-24 md:py-32 bg-card" data-testid="about-preview-section">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 md:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.7 }}
            >
              <div className="overflow-hidden rounded-lg">
                <img
                  src={c.about_preview_image || "https://static.wixstatic.com/media/d54d7c_60c0f08a40ae4e48b071a852b4de9075~mv2.jpg"}
                  alt="Gangadhar Som"
                  className="w-full h-auto transition-transform duration-700 hover:scale-105"
                />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.7 }}
              className="space-y-5 sm:space-y-6"
            >
              <span className="font-sans uppercase tracking-[0.2em] text-xs font-medium text-muted-foreground">
                {c.about_preview_label || 'About the Artist'}
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-light tracking-tight">
                {c.about_preview_heading || 'A Life Devoted to Art'}
              </h2>
              <p className="font-sans text-sm sm:text-base md:text-lg leading-relaxed text-foreground/80">
                {c.about_preview_text || 'Born in Podur, West Godavari, Andhra Pradesh, Gangadhar Som has created hundreds of artworks throughout his distinguished career. From film poster art to devotional masterpieces, his work spans multiple mediums and styles.'}
              </p>
              <Link
                to="/about" data-testid="read-more-button"
                className="inline-flex items-center gap-2 text-accent font-sans uppercase tracking-[0.2em] text-xs sm:text-sm font-medium hover:gap-4 transition-all"
              >
                {c.about_preview_cta || 'Read More'} <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
